/* Claude 3.61–3.73 — native basket drawer (replaces the Cartix app drawer).
 *
 * Defines <cart-drawer> with the public contract Dawn's product-form.js and
 * quick-add.js expect (getSectionsToRender / setActiveElement /
 * renderContents), so every existing add-to-basket flow opens this drawer with
 * zero changes to their code. Dawn's own cart-drawer.js only loads when
 * settings.cart_type == 'drawer' (it is 'page' here), so no define clash.
 *
 * NAMING: the element, the .ccd- classes and the /cart routes keep "cart"
 * because Dawn's contract and Shopify's endpoints depend on them. Every string
 * a CUSTOMER can read says "basket".
 *
 * ====================================================================
 * EVERY INTERACTIVE ELEMENT NEEDS A HANDLER HERE — AND A REAL CLICK TEST.
 * 3.61 SHIPPED TO THE LIVE STORE WITH THE GIFT-BOX TICK DOING NOTHING.
 * The section was changed from <button data-ccd-add> to
 * <input data-ccd-add-toggle> and this file was never updated, so the
 * checkbox had no handler at all. It RENDERED perfectly, which is exactly
 * why a render-only check passed it. Fixed in 3.62 by onChange below.
 * If you add a control to the section, add its branch here AND click it in
 * a browser before publishing.
 * ====================================================================
 *
 * ====================================================================
 * NOTHING WRITTEN DIRECTLY INTO THE DOM SURVIVES A CART CHANGE.
 * swapSections() REPLACES #ClaudeCartDrawer-Inner wholesale on every cart
 * request. Found in 3.63: entering a code the basket does not qualify for
 * did nothing visible. The error message WAS being written — and then a
 * third swapSections wiped it a moment later, because maybeAutoApply()
 * fires from inside swapSections, saw the still-inapplicable code and
 * removed it again. Any state that must outlive a re-render lives on the
 * ELEMENT (this._discountNote) and is repainted at the end of
 * swapSections. Do not go back to writing straight into the node.
 * ====================================================================
 *
 * ====================================================================
 * 3.70 — TWO AUTO-APPLIED CODES, AND SHOPIFY TAKES ONLY ONE.
 * BASKET10 (10% over £650) and SHEEP15 (15% off double sheepskins) can
 * both qualify on the same basket. A checkout accepts ONE discount code,
 * so maybeAutoApply picks a single target and actively removes the other
 * if it finds it there. Order is fixed, not clever: over the BASKET10
 * minimum BASKET10 wins, because 10% of £650+ always beats 15% of the
 * sheepskins alone. If you add a third code, work out its precedence
 * against the other two before wiring it — "whichever applied last" is
 * how a customer ends up with the smaller discount.
 * ====================================================================
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  const SECTIONS = 'claude-cart-drawer,cart-icon-bubble';
  const DISMISS_KEY = 'ccd-autoapply-dismissed';
  const R = window.routes || {};
  const URLS = {
    add: R.cart_add_url || '/cart/add',
    change: R.cart_change_url || '/cart/change',
    update: R.cart_update_url || '/cart/update',
  };

  /* 3.87 — products that only make sense ALONGSIDE a real item: packaging and
     the delivery upgrade. If a removal leaves nothing but these in the basket,
     sweepAddons() clears them too — nobody buys an empty box. Handles, not
     variant ids, so a new variant of the same product stays covered.

     3.91 — THIS LIST IS NOW ONLY A FALLBACK. The real handles come from
     data-addon-handles on #ClaudeCartDrawer-Inner, which the section writes
     from its own settings (see addonHandles() below). The comment above used
     to say "keep in sync with the section" — that instruction was the bug:
     the section read the handles from settings while this file hardcoded
     them, so a merchant renaming a handle in the theme editor would have
     silently switched the sweep off for that product with no visible sign.
     The constant survives for one case only: a cached copy of this script
     meeting a render that predates the data attribute. */
  const ADDON_HANDLES = ['luxury-gift-box', 'cardboard-package', 'tracked-shipping'];

  const money = (cents) => {
    const pounds = Math.floor(cents / 100).toLocaleString('en-GB');
    const pence = Math.round(cents % 100);
    return pence === 0 ? `£${pounds}` : `£${pounds}.${String(pence).padStart(2, '0')}`;
  };

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* noop */ } },
  };

  /* Codes the shopper removed by hand. Stored as a comma list because there is
     more than one auto-applied code now; the old single-value format parses
     as a one-item list, so nothing needs migrating. */
  const dismissed = {
    all() {
      return String(store.get(DISMISS_KEY) || '')
        .toLowerCase()
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    },
    has(code) { return this.all().indexOf(String(code).toLowerCase()) > -1; },
    add(code) {
      const list = this.all();
      const c = String(code).toLowerCase();
      if (list.indexOf(c) === -1) list.push(c);
      store.set(DISMISS_KEY, list.join(','));
    },
  };

  // 3.73 — bigger (16px) and thinner-stroke now that it sits inline beside
  // the price instead of on the photo; see .ccd-btn--add in the CSS.
  const PLUS_ICON =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M8 2v12M2 8h12"/></svg>';

  /* 3.96 — delivery-copy A/B test. One of four labels is dealt per visitor
     and persisted, so a returning shopper always sees the same wording.
     The letter is stamped on the cart as attribute _delivery_copy (the
     leading underscore hides it from the customer-facing order page) so
     every order records which copy its session saw. Analysis after one
     week picks the winner; then this block and the losing variants go.
     3.98 — v2: key bumped to ccd-copy-var2 and the attribute to
     _delivery_copy2 so everyone is re-dealt; the 3.96 week mixed two
     wordings under the same letters and its data is unusable. A is now
     the control — the closest one-line fit to the original Cartix
     wording. */
  const COPY_KEY = 'ccd-copy-var2';
  const COPY_VARIANTS = {
    A: { main: 'Upgrade to tracked & green', sub: '' },
    B: { main: 'Track my order', sub: 'DPD/Royal Mail' },
    C: { main: 'Track it, greener', sub: 'DPD/Royal Mail' },
    D: { main: 'Add tracking', sub: 'DPD/Royal Mail' },
  };
  const copyVariant = () => {
    let v = null;
    try { v = localStorage.getItem(COPY_KEY); } catch (e) { /* noop */ }
    if (!v || !COPY_VARIANTS[v]) {
      const keys = Object.keys(COPY_VARIANTS);
      v = keys[Math.floor(Math.random() * keys.length)];
      try { localStorage.setItem(COPY_KEY, v); } catch (e) { /* noop */ }
    }
    return v;
  };

  class ClaudeCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.upsellCache = {};
      this.activeElement = null;
      this._discountNote = null; // survives re-render — see header note
      // 3.73 — auto-close only ever arms for a session that began because an
      // item was just added to the basket (renderContents() is Dawn's
      // add-to-cart entry point). A manual open via the cart icon sets this
      // false and it stays false — including across the swapSections()
      // re-arm on later cart changes — until the drawer is closed and a
      // fresh add opens it again. See armAutoClose() below.
      this._autoCloseArmable = false;
      /* 3.85 — last time the shopper touched or scrolled INSIDE the panel.
         autoCloseTick's original guards (:hover, focused form control) are
         desktop-only signals: on a phone — 87% of sessions here — a shopper
         scrolling through the drawer triggers neither, so the 12s timer fired
         under their finger. See autoCloseTick(). */
      this._lastTouch = 0;
      /* 3.98 — "Complete the look" open/closed state. Held on the ELEMENT
         because nothing written into the DOM survives swapSections — same
         discipline as _discountNote. */
      this._ctlOpen = false;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      document.addEventListener('keyup', (e) => {
        if (e.code === 'Escape') this.close();
      });
      ['cart:refresh', 'cart:updated', 'cart:change'].forEach((evt) => {
        document.addEventListener(evt, () => this.refresh());
      });
      this.bindTouchActivity();
      this.bindCartIcon();
      this.maybeAutoApply();
      /* 3.98b — two ways an orphaned box used to reach checkout: a parent
         removed on the /cart page (Dawn's JS, which never calls this
         file), and a stale basket arriving on a fresh page load. The
         subscription hears the first the moment it happens; the load-time
         sweep catches the second, and costs nothing when the basket is
         empty or clean. */
      this.bindCartPageEvents();
      this.sweepAddons();
    }

    /* 3.98b — Dawn's basket page publishes PUB_SUB_EVENTS.cartUpdate on
       every quantity change and removal (see cart.js). Subscribing lets
       the sweep run the moment a parent is removed OUTSIDE the drawer,
       instead of waiting for the drawer's next open. Guarded: pubsub.js
       and constants.js normally load before this file, but if they have
       not, one retry after window load covers it. Our own sweep publishes
       with source 'claude-cart-drawer', ignored here — no loop. */
    bindCartPageEvents() {
      const bind = () => {
        if (this._pubsubBound) return;
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this._pubsubBound = true;
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'claude-cart-drawer') return;
          this.refresh();
          this.sweepAddons();
        });
      };
      bind();
      if (!this._pubsubBound) window.addEventListener('load', () => bind());
    }

    /* 3.85 — see _lastTouch. Passive listeners on the element itself; the
       scroll one is captured because scroll does not bubble from .ccd-body. */
    bindTouchActivity() {
      const mark = () => { this._lastTouch = Date.now(); };
      ['touchstart', 'touchmove', 'pointerdown', 'wheel'].forEach((evt) => {
        this.addEventListener(evt, mark, { passive: true });
      });
      this.addEventListener('scroll', mark, { passive: true, capture: true });
    }

    bindCartIcon() {
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActiveElement(icon);
        this._autoCloseArmable = false; // manual open — never auto-close this session
        this.open();
      });
    }

    getSectionsToRender() {
      return [
        { id: 'claude-cart-drawer', selector: '#ClaudeCartDrawer-Inner' },
        { id: 'cart-icon-bubble' },
      ];
    }

    setActiveElement(el) { this.activeElement = el; }

    renderContents(response) {
      // Dawn's add-to-cart entry point — the only path that arms auto-close.
      this._autoCloseArmable = true;
      if (response && response.sections) this.swapSections(response.sections);
      this.open();
    }

    open() {
      document.documentElement.classList.add('ccd-open');
      const panel = this.querySelector('.ccd-panel');
      if (panel) setTimeout(() => panel.focus({ preventScroll: true }), 60);
      this.renderUpsells();
      this.paintCopyLabel();
      this.stampCopyVariant();
      this.paintCtl();
      this.maybeAutoApply();
      // 3.87 — a basket emptied of real items OUTSIDE the drawer (the /cart
      // page fallback removes lines with Dawn's own JS, which never calls our
      // changeLine) would otherwise present its stranded add-ons on the next
      // open. Fire-and-forget; it early-returns without a request when the
      // server-rendered item count is 0.
      this.sweepAddons();
      if (this._autoCloseArmable) this.armAutoClose();
    }

    close() {
      if (!document.documentElement.classList.contains('ccd-open')) return;
      this.disarmAutoClose();
      this._autoCloseArmable = false; // the session that might have armed it is over
      document.documentElement.classList.remove('ccd-open');
      // a stale "that code didn't work" must not greet them on the next open
      this.discountMessage('', false);
      if (this.activeElement && this.activeElement.focus) this.activeElement.focus();
    }

    /* ---- AUTO-CLOSE — OFF unless the section setting turns it on ----
     * Deliberately conservative. A panel that shuts itself while someone is
     * reading the recommendations, comparing delivery options or halfway
     * through typing a code takes the decision out of their hands, and the
     * only recovery is to open it again and find their place. So the timer
     * restarts on every basket change, pauses while the pointer is over the
     * drawer, and pauses while anything inside it has focus. If it still
     * closes on someone, the setting is wrong, not the guard.
     *
     * 3.73 — also gated on this._autoCloseArmable: it only ever fires for a
     * session that began because an item was just added to the basket
     * (renderContents()), never one opened by hand via the cart icon, and it
     * does not re-arm mid-session if that session started manually — see the
     * constructor comment and the swapSections() call below. */
    armAutoClose() {
      this.disarmAutoClose();
      if (!this._autoCloseArmable) return;
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner) return;
      const secs = parseInt(inner.dataset.autocloseSeconds || '0', 10);
      if (!secs) return;
      this._autoCloseT = setTimeout(() => this.autoCloseTick(), secs * 1000);
    }

    disarmAutoClose() {
      if (this._autoCloseT) clearTimeout(this._autoCloseT);
      this._autoCloseT = null;
    }

    autoCloseTick() {
      if (!document.documentElement.classList.contains('ccd-open')) return;
      // :hover on the panel rather than a pair of enter/leave listeners —
      // <cart-drawer> itself spans the whole viewport when open, so listening
      // on it would report "hovering" for every position of the pointer.
      const panel = this.querySelector('.ccd-panel');
      if (panel && panel.matches(':hover')) { this.armAutoClose(); return; }
      // 3.85 — the touch equivalent of :hover. Someone who scrolled the drawer
      // in the last 6 seconds is still reading it; closing on them is the same
      // rudeness the hover guard exists to prevent, and on a phone it was the
      // ONLY thing this timer ever did to an engaged shopper.
      if (this._lastTouch && Date.now() - this._lastTouch < 6000) { this.armAutoClose(); return; }
      const a = document.activeElement;
      if (a && this.contains(a) && /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(a.tagName)) {
        this.armAutoClose();
        return;
      }
      this.close();
    }

    swapSections(sections) {
      const html = sections['claude-cart-drawer'];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#ClaudeCartDrawer-Inner');
        const current = this.querySelector('#ClaudeCartDrawer-Inner');
        if (fresh && current) {
          const keepExpress = current.querySelector('#ClaudeCartExpress');
          if (keepExpress) {
            const freshExpress = fresh.querySelector('#ClaudeCartExpress');
            if (freshExpress) freshExpress.replaceWith(keepExpress);
            else {
              const extras = fresh.querySelector('.ccd-extras');
              if (extras) extras.prepend(keepExpress);
            }
          }
          current.replaceWith(fresh);
          this.classList.toggle('is-empty', fresh.dataset.itemCount === '0');
        }
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const inner = doc.querySelector('#shopify-section-cart-icon-bubble');
        if (inner) bubble.innerHTML = inner.innerHTML;
      }
      this.paintDiscountMessage(); // the node it was written into is now gone
      this.paintNotice();          // ditto — see notice() above
      this.paintCopyLabel();
      this.stampCopyVariant();
      this.paintCtl();
      this.renderUpsells();
      this.maybeAutoApply();
      if (document.documentElement.classList.contains('ccd-open')) this.armAutoClose();
    }

    refresh() {
      clearTimeout(this._refreshT);
      this._refreshT = setTimeout(async () => {
        try {
          const res = await fetch(`/?sections=${SECTIONS}`, { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          this.swapSections(await res.json());
        } catch (e) { /* noop */ }
      }, 250);
    }

    setBusy(on) { this.classList.toggle('ccd-busy', on); }

    /* ---- 3.85: ONE SHARED STATUS LINE (.ccd-notice) ----
       Same discipline as _discountNote and for the same reason: swapSections
       replaces the whole inner wholesale, so the message is held on the ELEMENT
       and repainted after every render. Auto-expires so a stale failure cannot
       greet someone on their next open. */
    notice(text, isError) {
      this._notice = text ? { text: text, isError: !!isError, at: Date.now() } : null;
      this.paintNotice();
      clearTimeout(this._noticeT);
      if (text) this._noticeT = setTimeout(() => { this._notice = null; this.paintNotice(); }, 6000);
    }

    paintNotice() {
      const el = this.querySelector('.ccd-notice');
      if (!el) return;
      const n = this._notice;
      el.textContent = n ? n.text : '';
      el.hidden = !n;
      el.classList.toggle('ccd-notice--err', !!n && n.isError);
    }

    async cartRequest(url, payload) {
      this.setBusy(true);
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({}, payload, { sections: SECTIONS, sections_url: '/' })),
        });
        const data = await res.json();
        if (data && data.sections) this.swapSections(data.sections);
        return data;
      } catch (e) {
        console.warn('[claude-cart-drawer]', e);
        return null;
      } finally {
        this.setBusy(false);
      }
    }

    async changeLine(line, quantity) {
      if (!line || quantity < 0 || Number.isNaN(quantity)) return;
      const data = await this.cartRequest(URLS.change, { line, quantity });
      // 3.85 — a network blip used to return null here and simply do nothing
      // visible: the shopper taps + and the number does not move.
      if (!data) this.notice('Something went wrong — please try again.', true);
      // 3.87 — a removal may just have taken the last REAL item out, leaving
      // only packaging/delivery add-ons behind. Sweep them too.
      else if (quantity === 0) await this.sweepAddons();
      return data;
    }

    /* 3.91 — the handles the section actually rendered with. Falls back to
       the module constant only if the attribute is missing (a cached script
       against an older render). Blank entries are stripped so a merchant who
       empties one handle setting cannot turn every product into an add-on. */
    addonHandles() {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      const raw = (inner && inner.dataset.addonHandles) || '';
      const list = raw.split(',').map((s) => s.trim()).filter(Boolean);
      return list.length ? list : ADDON_HANDLES;
    }

    giftHandle() {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      return (inner && inner.dataset.giftHandle) || 'luxury-gift-box';
    }

    /* ---- 3.87: ADD-ONS NEVER OUTLIVE THE BASKET ----
       The luxury gift box, cardboard box and tracked-delivery upgrade are
       ordinary products, so removing the last real item used to leave them
       stranded — a shopper could check out with nothing but their own
       packaging. After any removal (and on open, for baskets emptied via the
       /cart page), if EVERY remaining line is an add-on, clear them all in
       one /cart/update. If any real item remains, this does nothing.
       _sweepBusy prevents re-entry: the sweep's own cartRequest triggers
       swapSections, and the basket it leaves behind is empty, so the next
       pass would early-return anyway — the flag just saves the round trip.

       ---- 3.91: AND THE GIFT BOX NEVER OUTLIVES THE BAG ----
       A second, narrower rule on top of the first. The £15 luxury gift box
       is sold to hold a bag; a basket that still has a rug in it is not
       empty, so the sweep above correctly leaves everything alone — and the
       box survived with nothing left to put in it. The drawer's gift-box
       ROW disappears in that state too (it is gated on has_bag), so the
       shopper was left paying £15 for a box with no control anywhere to
       take it off. Checked on product_type from /cart.js, lowercased, to
       match the same test the section makes in Liquid.

       Note the two rules are exclusive by construction: the first fires
       only when NO real item remains, the second only when at least one
       does. Both write into the same updates object, so either way this is
       a single /cart/update round trip. */
    async sweepAddons() {
      if (this._sweepBusy) return;
      this._sweepBusy = true;
      try {
        const inner = this.querySelector('#ClaudeCartDrawer-Inner');
        if (inner && inner.dataset.itemCount === '0') return; // already empty — no request
        const cart = await this.fetchCart();
        if (!cart || !cart.items || !cart.items.length) return;
        const handles = this.addonHandles();
        const giftHandle = this.giftHandle();
        const isAddon = (i) => handles.indexOf(i.handle) > -1;
        const real = cart.items.filter((i) => !isAddon(i));
        const updates = {};
        if (!real.length) {
          cart.items.forEach((i) => { updates[i.key] = 0; });
        } else {
          const hasBag = real.some((i) => String(i.product_type || '').toLowerCase() === 'bag');
          if (!hasBag) {
            cart.items.forEach((i) => { if (i.handle === giftHandle) updates[i.key] = 0; });
          }
          /* 3.98b — THE MISSING MIRROR: the cardboard box never outlives
             its parents either. Same qualification test the section runs
             in Liquid (type list OR title keyword, never a Bag), read from
             data attributes so there is no hand-copied list to go stale —
             the exact drift the 3.91 handles fix was about. */
          const packHandle = (inner && inner.dataset.packboxHandle) || 'cardboard-package';
          const packTypes = String((inner && inner.dataset.packboxTypes) || '')
            .toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
          const packWords = String((inner && inner.dataset.packboxKeywords) || '')
            .toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
          const wantsBox = (i) => {
            const t = String(i.product_type || '').toLowerCase();
            if (t === 'bag') return false;
            if (packTypes.indexOf(t) > -1) return true;
            const title = String(i.product_title || i.title || '').toLowerCase();
            return packWords.some((w) => title.indexOf(w) > -1);
          };
          if (!real.some(wantsBox)) {
            cart.items.forEach((i) => { if (i.handle === packHandle) updates[i.key] = 0; });
          }
        }
        if (!Object.keys(updates).length) return; // nothing stranded — no request
        const data = await this.cartRequest(URLS.update, { updates });
        /* 3.98b — tell the rest of the page. On /cart the shopper is
           looking at Dawn's own item list, which knows nothing about this
           sweep; publishing cartUpdate makes cart-items refetch and the
           orphan visibly disappear. Our own subscriber ignores this
           source, so no loop. */
        if (data && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'claude-cart-drawer', cartData: data });
        }
      } finally {
        this._sweepBusy = false;
      }
    }

    async addVariant(id) {
      if (!id || Number.isNaN(id)) return null;
      const data = await this.cartRequest(URLS.add, { items: [{ id, quantity: 1 }] });
      /* 3.85 — SAY SOMETHING WHEN IT FAILS. Every hide in this catalogue is a
         single piece, so "someone else bought it while you were looking" is a
         real, ordinary outcome, not an edge case. Until now it went to the
         console only: the + stayed disabled forever and the shopper was told
         nothing at all. Shopify returns 422 with a description for an
         unavailable variant; a null data means the request itself failed. */
      if (!data) {
        this.notice('Something went wrong — please try again.', true);
      } else if (data.status && data.status !== 200) {
        console.warn('[claude-cart-drawer] add failed:', data.description || data.message);
        this.notice("Sorry — that one's just gone.", true);
        this.refresh(); // re-render so the sold-out item stops offering itself
      }
      return data;
    }

    /* Remove whichever line holds this variant — the add-on toggles know a
       variant id but not a line number. */
    async removeVariant(id) {
      const cart = await this.fetchCart();
      if (!cart) return;
      const idx = (cart.items || []).findIndex((i) => i.variant_id === id);
      if (idx > -1) await this.changeLine(idx + 1, 0);
    }

    /* ---- the gift box checkbox ----
       Ticking adds the product, unticking removes it. THIS IS THE 3.62 FIX —
       data-ccd-add-toggle previously had no handler, so the tick was inert. */
    async toggleAddon(input) {
      const variant = parseInt(input.dataset.variant, 10);
      const wrap = input.closest('.ccd-addon');
      if (wrap) wrap.classList.toggle('is-on', input.checked);
      input.disabled = true;
      try {
        if (input.checked) await this.addVariant(variant);
        else await this.removeVariant(variant);
      } finally {
        input.disabled = false;
      }
    }

    /* ---- 3.70: the delivery choice ----
       Both radios carry the SAME variant — the tracked delivery product's.
       "Standard" is not a product, it is that line not being in the basket, so
       choosing it removes rather than adds. The section re-renders on the way
       back, which is what actually repaints the selected state; the class
       toggle here is only so the choice looks instant.
       3.96 — now also accepts the single checkbox row: add on tick, remove on
       untick; is-on follows checked. */
    async setDelivery(input) {
      const variant = parseInt(input.dataset.variant, 10);
      const isCheckbox = input.type === 'checkbox';
      const wantTracked = isCheckbox ? input.checked : input.dataset.ccdDelivery === 'tracked';
      const group = input.closest('.ccd-delivery');
      if (group) {
        group.querySelectorAll('.ccd-deliv').forEach((row) => {
          row.classList.toggle('is-on', isCheckbox ? (row.contains(input) && input.checked) : row.contains(input));
        });
        group.querySelectorAll('.ccd-deliv-input').forEach((i) => { i.disabled = true; });
      }
      try {
        if (wantTracked) await this.addVariant(variant);
        else await this.removeVariant(variant);
      } finally {
        if (group) group.querySelectorAll('.ccd-deliv-input').forEach((i) => { i.disabled = false; });
      }
    }

    /* ---- AUTO-APPLY A QUALIFYING DISCOUNT CODE ----
     * BASKET10 (10% over £650) and SHEEP15 (a product promotion) are CODES,
     * not automatic discounts, so nothing applies on its own.
     *
     * FOUR THINGS TO LEAVE ALONE:
     *
     * 1. THE EMPTY-BASKET EARLY RETURN (3.63). This runs from the constructor,
     *    i.e. on EVERY page load for EVERY visitor. Without the guard below it
     *    fired /cart.js on every pageview sitewide — for the large majority of
     *    visits, whose basket is empty and can never qualify. The item count is
     *    already in the DOM from the server render, so the check is free. Do
     *    not "simplify" this away.
     *
     * 2. MEASURE THE PRE-DISCOUNT TOTAL. Comparing against
     *    items_subtotal_price (post-discount) made a £700 basket qualify,
     *    apply, drop to £630, un-qualify, un-apply, return to £700 — an
     *    infinite loop for every basket between £650 and £722.
     *
     * 3. SHOPIFY'S VERDICT BEATS OUR ARITHMETIC. Gift-card-type products (the
     *    Luxury Gift Box is one) are excluded from discounts, so a basket can
     *    read £658 to us and £598 to the discount engine. If Shopify says
     *    inapplicable, remove the code and remember the basket state, or we
     *    re-apply immediately and start a second loop. The remembered state is
     *    keyed per code now that there is more than one.
     *
     * 4. ONE TARGET, AND THE OTHER COMES OFF (3.70). Only one code can be on a
     *    checkout. Working out the target is not enough — a code of ours left
     *    over from a bigger or smaller basket has to be actively removed, or
     *    it blocks the right one and the shopper silently gets the worse deal.
     *
     * This method also runs from swapSections, so it can remove a code the
     * customer just typed by hand — which is correct behaviour, but it means
     * an extra re-render lands AFTER applyDiscount has written its error.
     * That is why the message lives on the element, not in the DOM.
     */
    async maybeAutoApply() {
      if (this._autoBusy) return;
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner) return;

      // 3.63: an empty basket can never qualify — skip the /cart.js round trip.
      // Server-rendered, so it costs nothing and saves a request per pageview.
      if (inner.dataset.itemCount === '0') return;

      const baseCode = inner.dataset.autoapplyCode || '';
      const baseMin = parseInt(inner.dataset.autoapplyMin || '0', 10);
      const promoCode = inner.dataset.promoCode || '';
      const promoMax = parseInt(inner.dataset.promoMax || '0', 10);

      const managed = [baseCode, promoCode].filter(Boolean).map((c) => c.toLowerCase());
      if (!managed.length) return;
      const isOurs = (c) => managed.indexOf(String(c).toLowerCase()) > -1;

      this._autoBusy = true;
      try {
        const cart = await this.fetchCart();
        if (!cart) return;

        const entries = cart.discount_codes || [];
        const codes = entries.map((d) => d.code);
        const gross = cart.original_total_price; // pre-discount — see note 2
        const stateKey = `${cart.item_count}:${gross}`;

        // Precedence, see note 4. The basket code first: on a big basket 10%
        // of everything beats 15% of one product line.
        let target = '';
        if (baseCode && baseMin && gross >= baseMin) target = baseCode;
        else if (promoCode && (!promoMax || gross < promoMax)) target = promoCode;
        const targetLower = target.toLowerCase();

        const mine = entries.filter((d) => isOurs(d.code));
        const theirs = entries.filter((d) => !isOurs(d.code));

        const drop = (codeLower) =>
          this.cartRequest(URLS.update, {
            discount: codes.filter((c) => String(c).toLowerCase() !== codeLower).join(','),
          });

        const rejected = mine.find((d) => d.applicable === false);
        if (rejected) {
          this._rejectedState = `${stateKey}:${String(rejected.code).toLowerCase()}`; // note 3
          await drop(String(rejected.code).toLowerCase());
          return;
        }

        // one of ours that is no longer the right one — take it off
        const wrong = mine.find((d) => String(d.code).toLowerCase() !== targetLower);
        if (wrong) { await drop(String(wrong.code).toLowerCase()); return; }

        if (!target) return;
        if (mine.length) return;   // the target is already on the basket
        if (theirs.length) return; // never overwrite a code the customer typed
        if (dismissed.has(targetLower)) return;
        if (this._rejectedState === `${stateKey}:${targetLower}`) return;

        await this.cartRequest(URLS.update, { discount: codes.concat(target).join(',') });
      } finally {
        this._autoBusy = false;
      }
    }

    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        return await res.json();
      } catch (e) { return null; }
    }

    async getDiscountCodes() {
      const cart = await this.fetchCart();
      return cart ? (cart.discount_codes || []).map((d) => d.code) : [];
    }

    /* Record the note on the ELEMENT, then paint it. swapSections repaints it
       after every re-render — see the header note. */
    discountMessage(text, isError) {
      this._discountNote = text ? { text: text, isError: !!isError } : null;
      this.paintDiscountMessage();
    }

    paintDiscountMessage() {
      const el = this.querySelector('.ccd-discount-msg');
      if (!el) return;
      const note = this._discountNote;
      el.textContent = note ? note.text : '';
      el.hidden = !note;
      el.classList.toggle('ccd-msg--ok', !!note && !note.isError);
    }

    /* 3.96 — repaint the dealt delivery label. Called from open() and from
       swapSections(): NOTHING written directly into the DOM survives a cart
       re-render (see the header note), so the label is repainted after every
       swap, same discipline as the discount message and the notice line. */
    paintCopyLabel() {
      const el = this.querySelector('[data-ccd-copy-label]');
      if (!el) return;
      const c = COPY_VARIANTS[copyVariant()];
      el.innerHTML = c.sub ? `${esc(c.main)} <i>&middot; ${esc(c.sub)}</i>` : esc(c.main);
    }

    /* 3.96 — stamp the dealt letter on the cart, once per page load, only
       when the basket has something in it (an empty cart cannot hold
       attributes and can never convert anyway). Plain fetch WITHOUT the
       sections param on purpose: a cartRequest here would trigger
       swapSections, which calls this again — the _copyStamped flag plus the
       sectionless request keeps it to one quiet round trip. */
    async stampCopyVariant() {
      if (this._copyStamped) return;
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner || inner.dataset.itemCount === '0') return;
      this._copyStamped = true;
      try {
        await fetch(URLS.update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { _delivery_copy2: copyVariant() } }),
        });
      } catch (e) { this._copyStamped = false; }
    }

    /* 3.98 — repaint the "Complete the look" line + panel after every
       render. Open state lives on the element (_ctlOpen); the panel is
       re-filled lazily on first open and again after any cart change
       replaces it. */
    paintCtl() {
      const wrap = this.querySelector('[data-ccd-ctl-wrap]');
      if (!wrap) return;
      const btn = wrap.querySelector('[data-ccd-ctl-toggle]');
      if (btn) btn.setAttribute('aria-expanded', this._ctlOpen ? 'true' : 'false');
      const caret = wrap.querySelector('.ccd-ctl-caret');
      if (caret) caret.innerHTML = this._ctlOpen ? '&#9652;' : '&#9662;';
      /* 3.98b — the panel wrapper carries a server-rendered box-offer row
         (Option B), so visibility toggles on the WRAPPER while the lazy
         fill checks only the inner recommendations div. */
      const outer = wrap.querySelector('[data-ccd-ctl-panel]');
      if (outer) outer.hidden = !this._ctlOpen;
      const panel = wrap.querySelector('#ClaudeCartCtl');
      if (panel && this._ctlOpen && panel.childElementCount === 0) this.renderCtlRows(panel);
    }

    /* 3.98 — the panel's rows: up to 3 complementary recommendations for
       the newest basket item, same source and cache as the old strip
       (renderUpsells). Single-variant products add in place via the
       existing button[data-ccd-add] handler; anything with options links
       to its page — same mark, honest destination. */
    async renderCtlRows(panel) {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner) return;
      const sourceId = inner.dataset.sourceProduct;
      if (!sourceId) return;
      let inCart = [];
      try { inCart = JSON.parse(inner.dataset.cartProducts || '[]'); } catch (e) { /* noop */ }
      let products = this.upsellCache[sourceId];
      if (!products) {
        try {
          products = await this.fetchRecommendations(sourceId, 'complementary');
          if (!products.length) products = await this.fetchRecommendations(sourceId, 'related');
          this.upsellCache[sourceId] = products;
        } catch (e) { return; }
      }
      const items = products.filter((p) => p.available && !inCart.includes(p.id)).slice(0, 3);
      if (!items.length) {
        // 3.98b — if the panel already holds a box-offer row the silence is
        // fine; only an entirely empty panel needs to say something.
        const hasOffer = !!this.querySelector('[data-ccd-ctl-panel] .ccd-orow--pack');
        panel.innerHTML = hasOffer ? '' : '<p class="ccd-ctl-empty">Nothing to suggest just now.</p>';
        return;
      }
      panel.innerHTML = items.map((p) => {
        let img = p.featured_image || '';
        if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        const single = p.variants && p.variants.length === 1 && p.variants[0].available;
        const action = single
          ? `<button type="button" class="ccd-btn--add" data-ccd-add="${p.variants[0].id}" aria-label="Add ${esc(p.title)} to basket">${PLUS_ICON}</button>`
          : `<a class="ccd-btn--add" href="${esc(p.url)}" aria-label="Choose options for ${esc(p.title)}">${PLUS_ICON}</a>`;
        return (
          `<div class="ccd-ctl-row">` +
          `<a href="${esc(p.url)}" tabindex="-1">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" loading="lazy">` : ''}</a>` +
          `<span class="ccd-ctl-name">${esc(p.title)}</span>` +
          `<span class="ccd-ctl-price">${money(p.price)}</span>` +
          action +
          `</div>`
        );
      }).join('');
    }

    async applyDiscount(code) {
      const existing = await this.getDiscountCodes();
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
        this.discountMessage('That code is already applied.', true);
        return;
      }
      const data = await this.cartRequest(URLS.update, { discount: existing.concat(code).join(',') });
      if (!data) {
        this.discountMessage('Something went wrong — please try again.', true);
        return;
      }
      const entry = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (entry && entry.applicable === false) {
        await this.cartRequest(URLS.update, { discount: existing.join(',') });
        this.discountMessage("That code isn't valid for this basket.", true);
      } else {
        this.discountMessage('Discount applied.', false);
      }
    }

    async removeDiscount(code) {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      const ours = [
        inner && inner.dataset.autoapplyCode,
        inner && inner.dataset.promoCode,
      ].filter(Boolean).map((c) => c.toLowerCase());
      if (ours.indexOf(String(code).toLowerCase()) > -1) dismissed.add(code);
      const existing = await this.getDiscountCodes();
      const remaining = existing.filter((c) => c.toLowerCase() !== String(code).toLowerCase());
      this.discountMessage('', false);
      await this.cartRequest(URLS.update, { discount: remaining.join(',') });
    }

    async renderUpsells() {
      const track = this.querySelector('#ClaudeCartUpsells');
      if (!track || track.childElementCount > 0) return;
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner) return;
      const sourceId = inner.dataset.sourceProduct;
      if (!sourceId) return;
      let inCart = [];
      try { inCart = JSON.parse(inner.dataset.cartProducts || '[]'); } catch (e) { /* noop */ }

      let products = this.upsellCache[sourceId];
      if (!products) {
        try {
          products = await this.fetchRecommendations(sourceId, 'complementary');
          if (!products.length) products = await this.fetchRecommendations(sourceId, 'related');
          this.upsellCache[sourceId] = products;
        } catch (e) { return; }
      }
      let items = products.filter((p) => p.available && !inCart.includes(p.id)).slice(0, 8);

      /* 3.70 (cont.) — SHEEP15's rule, broadened for the upsell strip: anyone
         with ANY sheepskin in the basket (not only a double) sees the double
         promoted as the FIRST card, badged with the percent off. The product
         is fetched once and cached on the element (this._promoProduct) since
         it is a fixed product, not a per-source recommendation. Dropped
         entirely — not just skipped — if it is sold out or already in the
         basket, and a fetch failure here is a bonus lost, not a broken drawer. */
      const promoHandle = inner.dataset.hasSheepskin === 'true' ? inner.dataset.promoHandle || '' : '';
      let promo = null;
      if (promoHandle) {
        try {
          if (!this._promoProduct || this._promoProduct.handle !== promoHandle) {
            const res = await fetch(`/products/${promoHandle}.js`);
            this._promoProduct = res.ok ? await res.json() : null;
          }
          promo = this._promoProduct;
          if (promo && promo.available && !inCart.includes(promo.id)) {
            items = items.filter((p) => p.id !== promo.id);
            items.unshift(promo);
          } else {
            promo = null;
          }
        } catch (e) { promo = null; }
      }

      const wrap = this.querySelector('.ccd-upsells');
      if (!items.length) { if (wrap) wrap.hidden = true; return; }
      if (wrap) wrap.hidden = false;
      const percent = parseInt(inner.dataset.promoPercent || '0', 10) || 0;

      /* 3.72 (cont.) — SHEEP15 "Colour dots + corner clock". Both are
         Liquid-computed (see the section's data-promo-countdown /
         data-promo-colors) and only ever attached to the ONE promo card,
         never the ordinary recommendation cards — same isPromo gate the
         badge and was/now price already use. */
      const promoCountdown = inner.dataset.promoCountdown || '';
      let promoColors = [];
      try { promoColors = JSON.parse(inner.dataset.promoColors || '[]'); } catch (e) { /* noop */ }
      const promoColorsMore = parseInt(inner.dataset.promoColorsMore || '0', 10) || 0;

      track.innerHTML = items.map((p) => {
        const isPromoItem = !!(promo && p.id === promo.id);
        return this.upsellCard(
          p,
          isPromoItem ? percent : 0,
          isPromoItem ? promoCountdown : '',
          isPromoItem ? promoColors : [],
          isPromoItem ? promoColorsMore : 0
        );
      }).join('');
    }

    async fetchRecommendations(productId, intent) {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.products || [];
    }

    /* 3.73 — the + moved back OUT of the photo's corner (reverting 3.70/
       3.71's on-photo placement) to sit inline beside the price, inside
       .ccd-upsell-info's new .ccd-upsell-price-row. Still transparent with
       just a black glyph, only bigger now (16px, 1.4 stroke) since it no
       longer has to stay small enough to sit unobtrusively on a photo.
       Titles are still gone: the image and price carry the card.
       promoPercent > 0 marks the sheepskin-in-basket promo card (see
       renderUpsells) and adds a corner badge plus a was/now price; every
       other card ignores it. A product with one available variant adds
       straight to the basket; anything with options still has to go to its
       page to choose, so that case is an <a>, not a <button> — same mark,
       honest destination. The label lives in aria-label because the glyph
       carries no words.
       3.72 (cont.) — promoCountdown/promoColors/promoColorsMore are only
       ever non-empty for the ONE promo card (renderUpsells gates them on
       isPromoItem), so every ordinary recommendation card renders exactly
       as before: no countdown pill, no swatch row. Both come from Liquid
       (data-promo-countdown / data-promo-colors on #ClaudeCartDrawer-Inner)
       — see that section comment for why neither needed a hand-maintained
       colour map. */
    upsellCard(p, promoPercent, promoCountdown, promoColors, promoColorsMore) {
      let img = p.featured_image || '';
      if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=300';
      const singleVariant = p.variants && p.variants.length === 1 && p.variants[0].available;
      const action = singleVariant
        ? `<button type="button" class="ccd-btn--add" data-ccd-add="${p.variants[0].id}" aria-label="Add ${esc(p.title)} to basket">${PLUS_ICON}</button>`
        : `<a class="ccd-btn--add" href="${esc(p.url)}" aria-label="Choose options for ${esc(p.title)}">${PLUS_ICON}</a>`;
      const isPromo = promoPercent > 0;
      const priceHtml = isPromo
        ? `<span class="ccd-upsell-price--was">${money(p.price)}</span> ${money(Math.round((p.price * (100 - promoPercent)) / 100))}`
        : money(p.price);
      const countdownHtml = isPromo && promoCountdown
        ? `<span class="ccd-upsell-countdown">${esc(promoCountdown)}</span>`
        : '';
      const swatches = isPromo && promoColors && promoColors.length ? promoColors : [];
      const swatchesHtml = swatches.length
        ? `<div class="ccd-upsell-swatches">` +
          swatches.map((c) => `<span class="ccd-upsell-swatch${c.active ? ' is-on' : ''}" style="background-image:url('${esc(c.image)}')"></span>`).join('') +
          (promoColorsMore > 0 ? `<span class="ccd-upsell-swatch-more">+${promoColorsMore}</span>` : '') +
          `</div>`
        : '';
      return (
        `<div class="ccd-upsell-card${isPromo ? ' ccd-upsell-card--promo' : ''}">` +
        (isPromo ? `<span class="ccd-upsell-badge">${promoPercent}% off</span>` : '') +
        countdownHtml +
        `<div class="ccd-upsell-img">` +
        `<a href="${esc(p.url)}" tabindex="-1">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" loading="lazy">` : ''}</a>` +
        `</div>` +
        `<div class="ccd-upsell-info">` +
        `<div class="ccd-upsell-price-row">` +
        `<p class="ccd-upsell-price">${priceHtml}</p>` +
        action +
        `</div>` +
        swatchesHtml +
        `</div>` +
        `</div>`
      );
    }

    /* ---- events — every control in the section must appear in one of these ----
       Note: the "View full basket" link is a plain <a href="/cart"> and needs
       no handler; it must NOT be caught by onClick, which is why it carries no
       data- attribute. */
    onChange(e) {
      const delivery = e.target.closest('[data-ccd-delivery]');
      if (delivery) { this.setDelivery(delivery); return; }
      const addon = e.target.closest('[data-ccd-add-toggle]');
      if (addon) this.toggleAddon(addon);
    }

    onClick(e) {
      const ctl = e.target.closest('[data-ccd-ctl-toggle]');
      if (ctl) { this._ctlOpen = !this._ctlOpen; this.paintCtl(); return; }
      if (e.target.closest('[data-ccd-close]')) { this.close(); return; }
      const qtyBtn = e.target.closest('[data-ccd-qty]');
      if (qtyBtn) {
        this.changeLine(parseInt(qtyBtn.dataset.line, 10), parseInt(qtyBtn.dataset.ccdQty, 10));
        return;
      }
      const addBtn = e.target.closest('button[data-ccd-add]');
      if (addBtn) {
        addBtn.disabled = true;
        // 3.85 — ALWAYS re-enable. Previously this button was disabled on click
        // and nothing ever turned it back on, so a failed add left a dead grey
        // + on the card with no way to retry. On success the card is replaced
        // by the re-render anyway, so re-enabling is harmless there.
        this.addVariant(parseInt(addBtn.dataset.ccdAdd, 10)).finally(() => {
          addBtn.disabled = false;
        });
        return;
      }
      const tagX = e.target.closest('[data-ccd-remove-code]');
      if (tagX) this.removeDiscount(tagX.dataset.ccdRemoveCode);
    }

    onSubmit(e) {
      const form = e.target.closest('[data-ccd-discount-form]');
      if (!form) return;
      e.preventDefault();
      const input = form.querySelector('input');
      const code = (input.value || '').trim();
      if (!code) return;
      this.discountMessage('', false);
      this.applyDiscount(code).then(() => {
        const freshInput = this.querySelector('.ccd-discount-input');
        if (freshInput) freshInput.value = '';
      });
    }
  }

  customElements.define('cart-drawer', ClaudeCartDrawer);
})();
